import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { AdminApiError, type AdminClient } from "../api.js";
import { notPublishedReason } from "../console/data-source.js";
import type { AdminMonthsResponse, LedgerNotPublishedReason } from "../console/types.js";
import { clear, h, safeText } from "../dom.js";
import type {
  AdminHubIntakeResponse,
  CompanyHealth,
  CompanySlug,
  StaffRole,
} from "../types.js";
import { card, disclosure, emptyState, pageHeader } from "../ui/components.js";
import { monthText, pluralText, timeAgoText } from "../ui/display-words.js";
import { advice } from "../ui/capabilities.js";
import { COMPANY_HEADING_UNKNOWN_TEXT } from "../ui/face.js";
import { bankConnection, readerRole, type BankConnection } from "../ui/facts.js";
import { failureCopy } from "../ui/failure.js";
import { createFreshnessCaption } from "../ui/freshness.js";
import { navLabel, sentence } from "../ui/sentences.js";
import { defaultPeriod } from "../ui/period-control.js";
import { runAnnouncedAction, failureSentence } from "../ui/announced-action.js";
import { renderCheckNow } from "../ui/check-now.js";
import { HUB_INTAKE_NOT_SET_UP } from "../ui/hub-intake-words.js";
import {
  ACCOUNT_CONNECTED,
  BankInvitePossiblySent,
  BankWaitTimeout,
  BANK_WAIT_TIMEOUT_COPY,
  connectBankAccount,
  startAgainControl,
} from "./connect-account.js";
// One rule for "the last booking run": Home's flow tile and this page must not
// disagree about which run is the latest, or about the words it is said in.
import { bookingFailedCopy, latestProcessingRun } from "./home.js";

export interface HealthPageOptions {
  client: AdminClient;
  root: HTMLElement;
  /** Return false if a newer navigate superseded this render; drop results. */
  isCurrent?: () => boolean;
  /** When set, focus/scroll the matching company row (alert banner Details). */
  focusCompany?: CompanySlug;
  /** Company whose switches are shown first. Defaults to the focused company. */
  company?: CompanySlug;
  /** The role the shell already knows (decision 10: unknown reads as the AM). */
  role?: StaffRole;
  /** Injected clock for age formatting (tests). */
  now?: Date;
  /**
   * Ask the shell for a fresh fleet-health read (round 2, 1): a finished
   * repair must reach the banner and Home too, not just this page's re-render.
   */
  readHealth?: () => Promise<void>;
  /**
   * One visible sentence to paint above the fleet table in this render
   * (round 3, 3). Used by a finished repair, whose confirmation the re-render
   * would otherwise clear.
   */
  notice?: string;
}

/** Fleet health: per-company pipeline + connection status. */
export async function renderHealthPage(opts: HealthPageOptions): Promise<void> {
  const { client, root } = opts;
  const now = opts.now ?? new Date();
  clear(root);
  const page = h("div", { class: "settings-stack" });
  root.append(page);
  page.append(h("p", { class: "muted" }, ["Loading company health…"]));

  let companies: CompanyHealth[];
  const companyNames = new Map<string, string>();
  try {
    const [loaded, companyList] = await Promise.all([
      client.getFleetHealth(),
      client.listCompanies().catch(() => null),
    ]);
    companies = loaded.companies;
    for (const company of companyList?.companies ?? []) {
      companyNames.set(company.slug, amVisibleDisplayText(company.displayName));
    }
  } catch (error) {
    if (!(opts.isCurrent?.() ?? true)) return;
    if (
      error instanceof AdminApiError &&
      (error.status === 401 || error.status === 403 || error.status === 503)
    ) {
      throw error;
    }
    clear(page);
    const copy = failureCopy(error, { role: readerRole(opts.role), context: "read" });
    const retry = h(
      "button",
      { type: "button", class: "btn btn-primary", "data-action": "retry", "aria-label": "Retry" },
      ["Try again"],
    );
    retry.addEventListener("click", () => {
      void renderHealthPage(opts);
    });
    page.append(
      pageHeader(navLabel("health"), "Whether each company's books are updating."),
      h("div", { class: "error-state", role: "alert", "data-role": "health-error" }, [
        h("p", {}, [copy.sentence]),
        ...(copy.detail === null
          ? []
          : [disclosure("Show details", h("p", {}, [copy.detail]))]),
        retry,
      ]),
    );
    return;
  }
  if (!(opts.isCurrent?.() ?? true)) return;
  clear(page);

  const loadedAt = now;
  const freshnessHost = h("div", { "data-role": "freshness-host" });
  page.append(
    pageHeader(
      navLabel("health"),
      "See whether each company's books are updating, whether the bank connection is working, and what is switched on.",
    ),
    freshnessHost,
  );
  freshnessHost.append(
    createFreshnessCaption({
      updatedAt: loadedAt,
      onRefresh: () => {
        void renderHealthPage(opts);
      },
    }),
  );

  // Empty list is the full companies table (Worker no longer filters
  // is_active). This copy is true of that query, or it is not shown.
  if (companies.length === 0) {
    page.append(
      emptyState(
        `No companies are set up yet. Open ${navLabel("add-company")}, then return here to see whether its books are updating.`,
      ),
    );
    return;
  }

  // Round 3 (1): a Reconnect runs for up to thirty minutes and used to speak
  // only into the visually hidden region below. Its progress and failure are
  // said in the row itself, beside the button the person pressed.
  // Round 3 (3): a finished repair re-renders this page, which would clear the
  // confirmation along with everything else. The caller hands it back in.
  if (opts.notice !== undefined && opts.notice.length > 0) {
    page.append(
      h("p", {
        class: "banner banner-ok",
        "data-role": "health-notice",
      }, [opts.notice]),
    );
  }

  // "What is switched on" paints itself: its own three reads must never delay
  // the fleet table below, and a failed read only costs its own row's proof.
  const switchedHost = h("div", { "data-role": "switched-on-host" });
  page.append(switchedHost);
  paintSwitchedOn(switchedHost, {
    client,
    companies,
    companyNames,
    now,
    ...(opts.company !== undefined ? { company: opts.company } : {}),
    ...(opts.focusCompany !== undefined ? { focusCompany: opts.focusCompany } : {}),
    ...(opts.role !== undefined ? { role: opts.role } : {}),
    isCurrent: () => opts.isCurrent?.() ?? true,
  });

  const table = h("table", { class: "data-table fleet-table" });
  table.append(
    h("thead", {}, [
      h("tr", {}, [
        h("th", {}, ["Company"]),
        h("th", {}, ["Bank connection"]),
        h("th", {}, ["Recent updates"]),
      ]),
    ]),
  );
  const tbody = h("tbody", {});
  const rowRole: StaffRole = readerRole(opts.role);
  const live = h("div", {
    class: "aria-live",
    "aria-live": "polite",
    "data-role": "health-live",
  });
  page.append(live);
  let focusRow: HTMLTableRowElement | undefined;
  for (const company of companies) {
    const row = renderCompanyRow(company, now, companyNames, rowRole, {
      reconnect: (slug, item) =>
        healthReconnect(slug, item, live, opts),
      checkNow: (slug) =>
        rowRole === "owner" ? healthCheckNow(slug, opts) : null,
    });
    if (opts.focusCompany && company.company === opts.focusCompany) {
      row.tabIndex = -1;
      row.classList.add("focused-company");
      focusRow = row;
    }
    tbody.append(row);
  }
  table.append(tbody);
  page.append(card(h("div", { class: "table-scroll" }, [table])));
  if (focusRow) {
    const target = focusRow;
    queueMicrotask(() => {
      target.focus();
      target.scrollIntoView({ block: "nearest" });
    });
  }
}

// --- What is switched on ----------------------------------------------------
//
// Six rows, each proven ONLY from a response the console already reads. Where
// the response cannot tell us, the row says "Not shown here yet" — never a
// guess. The account manager gets one action ("Ask the owner"); the owner gets
// what the switch does, with where it lives behind "Show details" (the variable
// names are technical detail, so they never sit on the face).

export type SwitchState = "on" | "off" | "unknown";

export type SwitchId =
  | "books"
  | "booking"
  | "hub"
  | "close"
  | "statements"
  | "ai";

export interface SwitchedOnRow {
  id: SwitchId;
  label: string;
  state: SwitchState;
  /** The proof (on) or the honest reason it cannot be told (unknown). */
  evidence: string;
  /** Owner only: what this switch does, in one sentence. */
  ownerWhat?: string;
  /** Owner only: where it is turned on. Rendered inside "Show details". */
  whereOn?: string;
  /** The decided next step when this switch is off (round 2 M1's sentence). */
  offAdvice?: "switch_on_books_view" | "switch_on_hub_intake";
}

export interface SwitchedOnInputs {
  health: CompanyHealth | null;
  /**
   * Books view, from this month's ledger-accounts read: "off" only when the
   * Worker refused it for `ledger_view_not_enabled`; a month that was simply
   * never loaded proves nothing about the switch.
   */
  books:
    | { state: "on"; asOf: string }
    | { state: "off"; reason: LedgerNotPublishedReason }
    | { state: "unknown"; reason?: LedgerNotPublishedReason | null };
  /** The hub-intake read's hubSetup; null when that read failed. */
  hub: AdminHubIntakeResponse["hubSetup"] | null;
  /** The Months read; null when it failed. */
  months: AdminMonthsResponse | null;
  now: Date;
}

const NOT_SHOWN = "Not shown here yet.";

const BOOKS_WHAT =
  "Books view shows the saved copy of the books: every booked entry and its balance. It is off for this company.";
const BOOKS_WHERE =
  "Turn it on in GitHub: Settings, Secrets and variables, Actions, Variables. Set ENABLE_LEDGER_VIEW to true, add this company to LEDGER_VIEW_COMPANIES, then run the ledger view once.";
const HUB_WHAT =
  `${navLabel("receipt-shelf")} the hub sends book themselves. It is off for this company, so nothing from the hub books itself.`;
const HUB_WHERE =
  `A hub key is issued on ${navLabel("tokens")}. The company's hub settings are loaded outside the console.`;

/**
 * B2b-2: these switch rows are decided sentences, read from their one home.
 * Each is a `both(…)` row, so the reader's role cannot change the words.
 */
const BOOKS_VIEW_OFF = sentence("books_view_off", "owner");
const BOOKING_RUNNING = sentence("booking_running", "owner");
const BOOKING_FAILED_SHORT = sentence("booking_failed_short", "owner");
/**
 * The decided state word for a bank that needs a sign-in (the owner cell of
 * `bank_needs_reconnect`). The reader's own sentence for it is said once per
 * company on the ask line, so this word itself is role-free.
 */
const NEEDS_RECONNECT = sentence("bank_needs_reconnect", "owner");

/** Blocker keys the Months read uses for a bank statement check. */
const STATEMENT_BLOCKERS = [
  "statements_not_matching",
  "statements_to_confirm",
  "statements_saving",
] as const;

function booksRow(input: SwitchedOnInputs): SwitchedOnRow {
  const label = "Books view";
  if (input.books.state === "on") {
    const when = timeAgoText(input.books.asOf, input.now);
    return {
      id: "books",
      label,
      state: "on",
      evidence: when === "" ? "Books are loaded." : `Books were loaded ${when}.`,
    };
  }
  if (input.books.state === "off") {
    return {
      id: "books",
      label,
      state: "off",
      evidence: BOOKS_VIEW_OFF,
      ownerWhat: BOOKS_WHAT,
      whereOn: BOOKS_WHERE,
      offAdvice: "switch_on_books_view",
    };
  }
  const reason = input.books.reason ?? null;
  const why =
    reason === "ledger_not_published"
      ? "The books have not been loaded for this month, so we cannot tell whether the Books view is on."
      : "Nothing here says whether the Books view is on.";
  return { id: "books", label, state: "unknown", evidence: `${NOT_SHOWN} ${why}` };
}

function bookingRow(input: SwitchedOnInputs): SwitchedOnRow {
  const label = "Booking";
  if (input.health === null) {
    return {
      id: "booking",
      label,
      state: "unknown",
      evidence: `${NOT_SHOWN} Company health has not loaded.`,
    };
  }
  const run = latestProcessingRun(input.health.lastRuns);
  if (run === null) {
    return {
      id: "booking",
      label,
      state: "unknown",
      evidence: `${NOT_SHOWN} Booking has not run for this company, so we cannot tell whether it is on.`,
    };
  }
  const when = timeAgoText(run.startedAt, input.now);
  // A run proves booking is switched on, whatever it did. A run that failed or
  // is still going must not be described as a clean "last ran" — Home's band
  // says the same thing about the same run, and the two may not disagree.
  if (run.status === "running") {
    return { id: "booking", label, state: "on", evidence: BOOKING_RUNNING };
  }
  if (run.status === "failed") {
    return {
      id: "booking",
      label,
      state: "on",
      evidence:
        when === ""
          ? BOOKING_FAILED_SHORT
          : `${BOOKING_FAILED_SHORT.replace(/\.$/u, "")} (${when}).`,
    };
  }
  return {
    id: "booking",
    label,
    state: "on",
    evidence: when === "" ? "Booking has run for this company." : `Booking last ran ${when}.`,
  };
}

function hubRow(input: SwitchedOnInputs): SwitchedOnRow {
  const label = "Hub items";
  if (input.hub === null) {
    return {
      id: "hub",
      label,
      state: "unknown",
      evidence: `${NOT_SHOWN} The hub list did not load.`,
    };
  }
  if (input.hub === "receives") {
    return {
      id: "hub",
      label,
      state: "on",
      evidence: "The hub sends receipts and bills for this company.",
    };
  }
  if (input.hub === "not_set_up") {
    return {
      id: "hub",
      label,
      state: "off",
      evidence: HUB_INTAKE_NOT_SET_UP,
      ownerWhat: HUB_WHAT,
      whereOn: HUB_WHERE,
      offAdvice: "switch_on_hub_intake",
    };
  }
  return {
    id: "hub",
    label,
    state: "unknown",
    evidence: `${NOT_SHOWN} Nothing here says whether the hub sends to this company.`,
  };
}

function closeRow(input: SwitchedOnInputs): SwitchedOnRow {
  const label = "Month close";
  if (input.months === null) {
    return {
      id: "close",
      label,
      state: "unknown",
      evidence: `${NOT_SHOWN} The months did not load.`,
    };
  }
  const closed = input.months.months.find(
    (month) => month.status === "closed" || month.status === "closed_check_failed",
  );
  if (closed === undefined) {
    return {
      id: "close",
      label,
      state: "unknown",
      evidence: `${NOT_SHOWN} No month here has closed, so we cannot tell whether closing is on.`,
    };
  }
  const name = monthText(closed.period);
  return {
    id: "close",
    label,
    state: "on",
    evidence: name === "" ? "A month is closed." : `${name} is closed.`,
  };
}

function statementsRow(input: SwitchedOnInputs): SwitchedOnRow {
  const label = "Statement checking";
  if (input.months === null) {
    return {
      id: "statements",
      label,
      state: "unknown",
      evidence: `${NOT_SHOWN} The months did not load.`,
    };
  }
  for (const month of input.months.months) {
    for (const blocker of month.blockers) {
      if (!(STATEMENT_BLOCKERS as readonly string[]).includes(blocker.key)) continue;
      const count = Number.isInteger(blocker.count) && blocker.count > 0 ? blocker.count : 1;
      const evidence =
        blocker.key === "statements_not_matching"
          ? `${pluralText(count, "bank statement does", "bank statements do")}${sentence(
              "statements_not_match_tail",
              "owner",
            )}`
          : blocker.key === "statements_to_confirm"
            ? `${pluralText(count, "bank statement", "bank statements")} ${count === 1 ? "needs" : "need"} checking.`
            : `${navLabel("statements")} are saved on the next booking run.`;
      return { id: "statements", label, state: "on", evidence };
    }
  }
  return {
    id: "statements",
    label,
    state: "unknown",
    evidence: `${NOT_SHOWN} Nothing here says whether statement checking is on.`,
  };
}

function aiRow(): SwitchedOnRow {
  return {
    id: "ai",
    label: "AI suggestions",
    state: "unknown",
    evidence: `${NOT_SHOWN} Nothing here says whether AI suggestions are on.`,
  };
}

/** The six rows, in the order the owner reads them. */
export function switchedOnRows(input: SwitchedOnInputs): SwitchedOnRow[] {
  return [
    booksRow(input),
    bookingRow(input),
    hubRow(input),
    closeRow(input),
    statementsRow(input),
    aiRow(),
  ];
}

/**
 * The one sentence a row shows, in this person's words.
 *
 * Round 2 review, M1: the Books view and hub-intake switches are turned on
 * outside the console (GitHub variables and D1 settings), so both roles read
 * "This is switched on outside the console for now." — not decision 2's
 * ledger wording, which is for actions done in the ledger. `advice()` is the
 * one home of the sentence.
 */
export function switchSentence(row: SwitchedOnRow, role: StaffRole): string {
  if (row.state !== "off") return row.evidence;
  if (row.offAdvice === undefined) return row.evidence;
  return advice(row.offAdvice, role);
}

/** The state chip word (polish rules: plain words, no codes). */
export function switchStateWord(state: SwitchState): string {
  if (state === "on") return "On";
  if (state === "off") return "Off";
  return "Not shown here yet";
}

interface SwitchedOnPaintOptions {
  client: AdminClient;
  companies: readonly CompanyHealth[];
  companyNames: ReadonlyMap<CompanySlug, string>;
  now: Date;
  company?: CompanySlug;
  focusCompany?: CompanySlug;
  role?: StaffRole;
  isCurrent: () => boolean;
}

/** One company's six switches. Its own three reads; failures cost one row each. */
function paintSwitchedOn(host: HTMLElement, opts: SwitchedOnPaintOptions): void {
  clear(host);
  const first: CompanySlug = opts.companies[0]?.company ?? "";
  const preferred: CompanySlug = opts.company ?? opts.focusCompany ?? first;
  const chosen = opts.companies.some((row) => row.company === preferred)
    ? preferred
    : first;
  const labelOf = (slug: CompanySlug): string =>
    opts.companyNames.get(slug) ?? "this company";

  const section = h("section", { class: "card switched-on", "data-role": "switched-on" }, [
    h("h2", { class: "section-title" }, ["What is switched on"]),
  ]);
  host.append(section);

  const chooserId = "switched-on-company";
  const chooser = h(
    "select",
    {
      class: "input",
      id: chooserId,
      "data-role": "switched-company",
      "aria-label": "Company to show the switches for",
    },
    opts.companies.map((row) => {
      // The label is written as text after the element: a slug expression sitting
      // inside an array that feeds h() is what the brand guard cannot tell from
      // a leak, and the display words come from the one name map.
      const option = h(
        "option",
        { value: row.company, ...(row.company === chosen ? { selected: "" } : {}) },
        [],
      );
      option.textContent = labelOf(row.company);
      return option;
    }),
  );
  chooser.addEventListener("change", () => {
    paintSwitchedOn(host, { ...opts, company: chooser.value });
  });
  section.append(
    h("div", { class: "switched-on-head" }, [
      h("label", { class: "caption", for: chooserId }, ["Company"]),
      chooser,
    ]),
  );

  const body = h("div", { "data-role": "switched-on-body" });
  section.append(body);
  body.append(h("p", { class: "muted" }, ["Checking what is on…"]));

  const role: StaffRole = readerRole(opts.role);
  const period = defaultPeriod(opts.now);

  void (async (): Promise<void> => {
    const [books, hub, months] = await Promise.all([
      readBooks(opts.client, chosen, period),
      readHub(opts.client, chosen),
      readMonths(opts.client, chosen),
    ]);
    if (!opts.isCurrent()) return;
    const rows = switchedOnRows({
      health: opts.companies.find((row) => row.company === chosen) ?? null,
      books,
      hub,
      months,
      now: opts.now,
    });
    clear(body);
    const list = h("ul", { class: "switched-on-list" });
    for (const row of rows) {
      const item = h("li", {
        class: "switched-on-row",
        "data-role": "switch-row",
        "data-switch": row.id,
        "data-state": row.state,
      }, [
        h("p", { class: "switched-on-name" }, [
          h("span", {}, [row.label]),
          h(
            "span",
            { class: `chip chip-${row.state === "on" ? "ok" : row.state === "off" ? "warn" : "info"}`, "data-role": "switch-state" },
            [switchStateWord(row.state)],
          ),
        ]),
        h("p", { class: "switched-on-sentence", "data-role": "switch-sentence" }, [
          switchSentence(row, role),
        ]),
      ]);
      if (row.state === "off" && role === "owner" && row.whereOn !== undefined) {
        // The owner keeps what the switch does and where it lives: decision 2's
        // sentence is the next step, and this is the detail behind it.
        const detail =
          row.ownerWhat === undefined
            ? h("p", {}, [row.whereOn])
            : h("div", {}, [h("p", {}, [row.ownerWhat]), h("p", {}, [row.whereOn])]);
        item.append(disclosure("Show details", detail));
      }
      list.append(item);
    }
    body.append(list);
  })();
}

async function readBooks(
  client: AdminClient,
  company: CompanySlug,
  period: string,
): Promise<SwitchedOnInputs["books"]> {
  try {
    const response = await client.getLedgerAccounts(company, period);
    return { state: "on", asOf: response.header.asOf };
  } catch (error) {
    const reason = notPublishedReason(error);
    if (reason === "ledger_view_not_enabled") return { state: "off", reason };
    return { state: "unknown", reason };
  }
}

async function readHub(
  client: AdminClient,
  company: CompanySlug,
): Promise<SwitchedOnInputs["hub"]> {
  try {
    const response = await client.listHubIntake(company, { limit: 0 });
    return response.hubSetup;
  } catch {
    return null;
  }
}

async function readMonths(
  client: AdminClient,
  company: CompanySlug,
): Promise<AdminMonthsResponse | null> {
  try {
    return await client.getMonths(company);
  } catch {
    return null;
  }
}

/**
 * Compact relative age from an ISO timestamp (m / h / d).
 * Mirror: a genuine fresh stamp still reads short ("0m" / "1m"), not stuck.
 */
export function formatHealthAge(iso: string, now = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "unknown age";
  const ms = Math.max(0, now.getTime() - then);
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${String(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${String(hours)}h`;
  const days = Math.floor(hours / 24);
  return `${String(days)}d`;
}

/**
 * Format one run for the Health cell.
 * Status (+ errorCode) first; volume as a qualifier so a failed run with
 * real work cannot read as success (R14O-6 / R17O-13); age so a three-week
 * stuck `running` cannot read as fresh (R20O-2).
 * null/undefined volume → "volume unknown"; real 0 stays "0 processed".
 */
export function formatHealthRun(
  run: CompanyHealth["lastRuns"][number],
  now = new Date(),
): string {
  const base = `${amVisibleDisplayText(run.runType)}=${amVisibleDisplayText(run.status)}`;
  const withCode = run.errorCode ? `${base} (${amVisibleDisplayText(run.errorCode)})` : base;
  const volume = formatRunVolume(run.txnsProcessed, run.queueAdded);
  const age = formatHealthAge(run.startedAt, now);
  if (run.status === "running") {
    // Present-tense status needs duration: "running for 21d" vs "for 0m".
    return `${withCode} for ${age} · ${volume}`;
  }
  // Settled runs: age as a quiet qualifier (not a banner).
  return `${withCode} · ${volume} · ${age} ago`;
}

function formatRunVolume(
  txnsProcessed: number | null | undefined,
  queueAdded: number | null | undefined,
): string {
  const processedUnknown =
    txnsProcessed === null || txnsProcessed === undefined;
  const queuedUnknown = queueAdded === null || queueAdded === undefined;
  if (processedUnknown && queuedUnknown) {
    return "volume unknown";
  }
  const processed = processedUnknown ? "unknown" : String(txnsProcessed);
  const queued = queuedUnknown ? "unknown" : String(queueAdded);
  return `${processed} processed / ${queued} queued`;
}

/** Plaid connection line — status plus last-sync age when the wire has it. */
export function formatHealthConnection(
  item: CompanyHealth["plaidItems"][number],
  now = new Date(),
): string {
  const inst = safeText(amVisibleDisplayText(item.institution ?? ""), amVisibleDisplayText(item.plaidItemId));
  const state = amVisibleDisplayText(item.status);
  if (item.lastSyncedAt) {
    return `${inst}: ${state} · synced ${formatHealthAge(item.lastSyncedAt, now)} ago`;
  }
  return `${inst}: ${state}`;
}

function connectionFace(
  item: CompanyHealth["plaidItems"][number],
  now: Date,
): string {
  const name = safeText(amVisibleDisplayText(item.institution ?? ""), "Bank");
  const age = item.lastSyncedAt
    ? ` Last updated ${formatHealthAge(item.lastSyncedAt, now)} ago.`
    : "";
  if (item.status === "healthy") return `${name}: Connected.${age}`;
  // round-11 item 26: the decided word is "Needs reconnecting", not a sign-in
  // and not a second spelling for a revoked or broken bank.
  return `${name}: ${NEEDS_RECONNECT}${age}`;
}

function runFace(run: CompanyHealth["lastRuns"][number], role: StaffRole, now: Date): string {
  const when = timeAgoText(run.startedAt, now);
  // rd R8-13: the same instant reads the same on Home, Books and here (the
  // console's one phrase), never a UTC clock time with no date.
  const phrase = when === "" ? "at an unknown time" : when;
  const label =
    run.runType === "processing"
      ? "Last booking run"
      : run.runType === "plaid_sync"
        ? "Last bank update"
        : run.runType === "reporting"
          ? "Last report run"
          : "Last update";
  if (run.status === "running") {
    return `${label} started ${phrase}. Still running.`;
  }
  if (run.status === "succeeded") {
    return `${label} finished ${phrase}.`;
  }
  // Round 2 (5): a failed BOOKING run is the state the shared sentences decide
  // (rd-z-fixes-background §3), and Home says the same words about the same
  // run. Other kinds of run have no shared sentence.
  if (run.runType === "processing") {
    return `${label}: ${phrase}. ${bookingFailedCopy(role)}`;
  }
  // The reader can press Check now (B2b-3), so the next step is that control for
  // both roles — never "ask the owner" for something the AM can do.
  return `${label}: ${phrase}. Did not finish. ${advice("check_now", role)}`;
}

/**
 * Round 3 (4): the reconnect wait's own timeout, written by
 * `pollForConnectedItem` (connect-account.ts) and never by the server. It is
 * not a failed save — the bank may have connected just after we stopped
 * watching — so the person is told what to check instead.
 *
 * Round 5 (1): this used to be a local copy of the sentence, so when R6b
 * reworded the wait the row kept saying the old words and the timeout was
 * still recognised only by its opening line. Both now come from the one
 * exported constant the wait itself throws, so the row cannot disagree with
 * the flow that timed out.
 */
const RECONNECT_TIMED_OUT = BANK_WAIT_TIMEOUT_COPY;

/**
 * Round 5 (1): an exact match on the constant, not a prefix. A prefix match
 * silently keeps "working" across a reword while the copy drifts; an exact
 * match fails closed — an unrecognised timeout falls back to the decided
 * failure sentence rather than to a sentence no one decided.
 */
function isReconnectTimeout(error: unknown): boolean {
  return error instanceof BankWaitTimeout;
}

/** The failure face for a Reconnect: its own timeout, else the decided sentence. */
function reconnectFailureCopy(error: unknown): string {
  if (isReconnectTimeout(error)) return RECONNECT_TIMED_OUT;
  // Round 2 review, M3: a blocked invitation says so, not "That did not save."
  if (error instanceof BankInvitePossiblySent) return error.sentence;
  return failureSentence(error);
}

/**
 * C-10: the owner's Reconnect for one bank connection, the same invitation
 * flow Bank accounts uses. The account manager gets the sentence instead,
 * because issuing a bank invitation is the owner's.
 */
function healthReconnect(
  company: CompanySlug,
  item: CompanyHealth["plaidItems"][number],
  live: HTMLElement,
  opts: HealthPageOptions,
): HTMLElement {
  const host = h("div", { "data-role": "health-reconnect" });
  const name = safeText(amVisibleDisplayText(item.institution ?? ""), "Bank");
  const button = h(
    "button",
    {
      type: "button",
      class: "btn btn-secondary",
      "data-action": "reconnect-bank",
      "aria-label": `Reconnect ${name}`,
    },
    ["Reconnect"],
  );
  // Round 3 (1): the progress and the failure are said here, in the row the
  // person acted in — a banner at the top of a long fleet table is not on
  // screen when the row is.
  // Not live regions: the page's hidden region already announces these
  // sentences, and a second announcer would say each of them twice.
  const status = h("p", {
    class: "muted caption",
    "data-role": "health-reconnect-status",
    hidden: "true",
  });
  const error = h("p", {
    class: "banner banner-err",
    "data-role": "health-reconnect-error",
    hidden: "true",
  });
  button.addEventListener("click", () => {
    const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
    hideReconnectError(error);
    setReconnectStatus(status, "Preparing connection…");
    // A blocked popup's URL is the only way to finish, so it stays on screen
    // while the wait's progress lines go by (round 3, 1).
    let blockedUrl: string | null = null;
    void runAnnouncedAction(live, stillCurrent, "Reconnect", async () => {
      button.disabled = true;
      try {
        await connectBankAccount({
          client: opts.client,
          company,
          scope: host,
          // Round 4 (1): this is a RECONNECT of one named connection, so the
          // wait must resolve on THAT item turning healthy — never on another
          // bank in the same company healing while this one stays broken
          // (R6-14). The institution is the fallback when the repair comes
          // back under a new item id.
          expectedItemId: item.plaidItemId,
          expectedInstitution: item.institution ?? null,
          onStatus: (message) => {
            if (!stillCurrent()) return;
            // Round 3 (1): the URL of a blocked popup and the long wait must
            // be readable on screen, not only announced to a screen reader.
            live.textContent = message;
            if (message.startsWith("Popup blocked.")) blockedUrl = message;
            setReconnectStatus(status, blockedUrl ?? message);
          },
        });
        if (!stillCurrent()) return;
        live.textContent = ACCOUNT_CONNECTED;
        setReconnectStatus(status, null);
        // Round 2 (1): the shell's reading (and so the banner and Home) is
        // older than the repair this flow just saw.
        await opts.readHealth?.();
        if (!stillCurrent()) return;
        // Round 3 (3): the re-render clears this page, so the confirmation is
        // handed to it rather than vanishing with the row that caused it.
        await renderHealthPage({ ...opts, notice: ACCOUNT_CONNECTED });
      } catch (caught) {
        // Round 3 (1): an abandoned flow used to re-enable the button in
        // silence. Say on screen what happened.
        if (stillCurrent()) {
          setReconnectStatus(status, null);
          // Round 2 (7): the decided failure sentence, never the server's words.
          // Round 3 (4): a wait that timed out keeps its own locally written
          // guidance — the bank may have connected, so it is not a failed save.
          const copy = reconnectFailureCopy(caught);
          showReconnectError(error, copy);
          // Round 2 review, M3: a lost invite response blocks a second mint;
          // "Start again" clears the mark so the next press is a first try.
          if (caught instanceof BankInvitePossiblySent) {
            error.append(document.createTextNode(" "), startAgainControl(company, () => {
              hideReconnectError(error);
            }));
          }
          // Round 4 (2): the shared announcement region still held the wait's
          // last progress line ("Waiting for the bank to finish…") after every
          // failure, so the page's announced state contradicted its own error
          // row. A timeout never throws past `work` (see below), so this is the
          // only place its sentence reaches the live region. For every other
          // failure this writes the same sentence `runAnnouncedAction` does.
          live.textContent = copy;
        }
        // Round 3 (4): the timeout's next step is already on screen. Rethrowing
        // would make the shared announcer add "That did not save." beside it,
        // which is exactly the false claim this sentence exists to avoid.
        if (isReconnectTimeout(caught)) return;
        // The same for a blocked invitation: its sentence and "Start again"
        // are already in the row.
        if (caught instanceof BankInvitePossiblySent) return;
        throw caught;
      } finally {
        if (stillCurrent()) button.disabled = false;
      }
    });
  });
  host.append(button, status, error);
  return host;
}

/** Visible progress line for a Reconnect on Connection status (round 3, 1). */
function setReconnectStatus(node: HTMLElement, message: string | null): void {
  clear(node);
  if (message === null || message.length === 0) {
    node.hidden = true;
    node.setAttribute("hidden", "true");
    return;
  }
  node.hidden = false;
  node.removeAttribute("hidden");
  node.append(document.createTextNode(message));
}

/** Visible failure of a Reconnect on Connection status (round 3, 1). */
function showReconnectError(node: HTMLElement, message: string): void {
  clear(node);
  node.hidden = false;
  node.removeAttribute("hidden");
  node.append(document.createTextNode(message));
}

function hideReconnectError(node: HTMLElement): void {
  clear(node);
  node.hidden = true;
  node.setAttribute("hidden", "true");
}

/**
 * C-12: the owner's failed-booking sentence says to press Check now, so the
 * control is here, for that company, instead of a dead end.
 */
function healthCheckNow(company: CompanySlug, opts: HealthPageOptions): HTMLElement {
  const host = h("div", { "data-role": "health-check-now" });
  renderCheckNow({
    client: opts.client,
    company,
    root: host,
    stillCurrent: () => opts.isCurrent?.() ?? true,
  });
  return host;
}

function renderCompanyRow(
  company: CompanyHealth,
  now: Date,
  companyNames: ReadonlyMap<CompanySlug, string>,
  role: StaffRole,
  actions: {
    reconnect: (slug: CompanySlug, item: CompanyHealth["plaidItems"][number]) => HTMLElement;
    checkNow: (slug: CompanySlug) => HTMLElement | null;
  },
): HTMLTableRowElement {
  const connections = h("td", { "data-role": "connections" });
  let asksOwner = false;
  // B2b-1: "is there a bank" is the shared fact, never this page's own read.
  const bank: BankConnection = bankConnection(company);
  if (bank === "none") {
    connections.append(
      // The decided sentence, then this reader's next step: Connect account is
      // the owner's, so the account manager is told who can.
      h("p", {}, [
        role === "owner"
          ? `${sentence("no_bank_connected", role)} Connect a bank from ${navLabel("balances")}.`
          : sentence("no_bank_connected", role),
      ]),
    );
  } else {
    for (const item of company.plaidItems) {
      connections.append(
        h("p", {}, [
          connectionFace(item, now),
          disclosure("Show details", h("p", {}, [formatHealthConnection(item, now)])),
        ]),
      );
      // C-10: this page described the problem and offered nothing to fix it.
      // The owner gets the same Reconnect action Bank accounts has; the
      // account manager is told who can reconnect.
      if (item.status !== "healthy") {
        if (role === "owner") {
          connections.append(actions.reconnect(company.company, item));
        } else {
          asksOwner = true;
        }
      }
    }
    // Round 2 (8): one sentence per company, not one per broken connection.
    if (asksOwner) {
      connections.append(
        h("p", { "data-role": "health-reconnect-ask" }, [
          sentence("bank_needs_reconnect", role),
        ]),
      );
    }
  }

  const runs = h("td", { "data-role": "runs" });
  // rc-4 B4 (HIGH): a company with no Plaid item must never be described by a
  // bank update from before it was ever linked. The bank's own run rows are
  // shown only when `bankConnection` says a bank exists (still true for a bank
  // that has never produced a reading).
  const visibleRuns =
    bank === "none"
      ? company.lastRuns.filter((run) => run.runType !== "plaid_sync")
      : company.lastRuns;
  if (visibleRuns.length === 0) {
    runs.append(
      h("p", {}, [
        bank === "none"
          ? "No updates yet."
          : "No updates yet. They appear here after the next bank update.",
      ]),
    );
  } else {
    for (const run of visibleRuns) {
      runs.append(
        h("p", {}, [
          runFace(run, role, now),
          disclosure("Show details", h("p", {}, [formatHealthRun(run, now)])),
        ]),
      );
    }
  }
  // C-12: "The last booking run failed." is the end of the line for the owner
  // unless the action it names is here. Check now is the same control Home and
  // Bank accounts carry; it appears only where a booking run failed.
  const latest = latestProcessingRun(company.lastRuns);
  if (latest !== null && latest.status === "failed") {
    const control = actions.checkNow(company.company);
    if (control !== null) runs.append(control);
  }

  return h("tr", { "data-company": company.company }, [
    h("td", {}, [
      companyNames.get(company.company) ?? COMPANY_HEADING_UNKNOWN_TEXT,
    ]),
    connections,
    runs,
  ]);
}
